/**
 * Start-up timing (wave 4 stage H, perf-render): time from "start game" to the first frame.
 *
 *   start → mapLoaded → simReady → terrainPrebuilt | terrainMainThread → rendererReady → firstFrame
 *
 * Each step is a User Timing mark (`cq:startup:<step>`, visible in DevTools) and is kept for
 * the perf overlay (`startupReport()`); development builds log the report once per game.
 * A new "start" begins a new report.
 */

export type StartupStep = "start" | "mapLoaded" | "simReady" | "terrainPrebuilt" | "terrainMainThread" | "rendererReady" | "firstFrame";

export interface StartupReport {
  /** ms since "start" per step, in the order they happened. */
  steps: { step: StartupStep; at: number }[];
  /** ms from "start" to "firstFrame" (0 until both happened). */
  total: number;
}

let t0 = -1;
let steps: { step: StartupStep; at: number }[] = [];
let logged = false;

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

export function markStartup(step: StartupStep, at = now()): void {
  if (step === "start") {
    t0 = at;
    steps = [];
    logged = false;
  } else if (t0 < 0) {
    // A renderer created without a session start (dev harness, tests): time from itself.
    t0 = at;
  }
  if (step === "firstFrame" && steps.some((s) => s.step === "firstFrame")) return;
  steps.push({ step, at: at - t0 });
  try {
    performance.mark(`cq:startup:${step}`);
  } catch {
    /* no User Timing */
  }
  if (step === "firstFrame" && !logged) {
    logged = true;
    let dev = false;
    try {
      dev = !!import.meta.env?.DEV;
    } catch {
      dev = false;
    }
    if (dev && typeof console !== "undefined") console.info("[perf] start-up", steps.map((s) => `${s.step} ${Math.round(s.at)} ms`).join(" · "));
  }
}

export function startupReport(): StartupReport {
  const first = steps.find((s) => s.step === "firstFrame");
  return { steps: steps.slice(), total: first ? first.at : 0 };
}
